import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, Plus, Trash2, Sparkles, Layers, Building2, MapPin, X } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { useUser } from "@clerk/clerk-react";
import { formatPhoneNumber } from "@/lib/phoneUtils";

// US States for dropdown
const US_STATES = [
  { name: "Alabama", code: "AL" },
  { name: "Alaska", code: "AK" },
  { name: "Arizona", code: "AZ" },
  { name: "Arkansas", code: "AR" },
  { name: "California", code: "CA" },
  { name: "Colorado", code: "CO" },
  { name: "Connecticut", code: "CT" },
  { name: "Delaware", code: "DE" },
  { name: "Florida", code: "FL" },
  { name: "Georgia", code: "GA" },
  { name: "Hawaii", code: "HI" },
  { name: "Idaho", code: "ID" },
  { name: "Illinois", code: "IL" },
  { name: "Indiana", code: "IN" },
  { name: "Iowa", code: "IA" },
  { name: "Kansas", code: "KS" },
  { name: "Kentucky", code: "KY" },
  { name: "Louisiana", code: "LA" },
  { name: "Maine", code: "ME" },
  { name: "Maryland", code: "MD" },
  { name: "Massachusetts", code: "MA" },
  { name: "Michigan", code: "MI" },
  { name: "Minnesota", code: "MN" },
  { name: "Mississippi", code: "MS" },
  { name: "Missouri", code: "MO" },
  { name: "Montana", code: "MT" },
  { name: "Nebraska", code: "NE" },
  { name: "Nevada", code: "NV" },
  { name: "New Hampshire", code: "NH" },
  { name: "New Jersey", code: "NJ" },
  { name: "New Mexico", code: "NM" },
  { name: "New York", code: "NY" },
  { name: "North Carolina", code: "NC" },
  { name: "North Dakota", code: "ND" },
  { name: "Ohio", code: "OH" },
  { name: "Oklahoma", code: "OK" },
  { name: "Oregon", code: "OR" },
  { name: "Pennsylvania", code: "PA" },
  { name: "Rhode Island", code: "RI" },
  { name: "South Carolina", code: "SC" },
  { name: "South Dakota", code: "SD" },
  { name: "Tennessee", code: "TN" },
  { name: "Texas", code: "TX" },
  { name: "Utah", code: "UT" },
  { name: "Vermont", code: "VT" },
  { name: "Virginia", code: "VA" },
  { name: "Washington", code: "WA" },
  { name: "West Virginia", code: "WV" },
  { name: "Wisconsin", code: "WI" },
  { name: "Wyoming", code: "WY" },
];

interface EditProfileSheetProps {
  isOpen: boolean;
  onClose: () => void;
  companyData: {
    name: string;
    contactName: string;
    jobTitle: string;
    contactEmail: string;
    contactPhone: string;
    logo: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    email: string;
    website: string;
    description: string;
    yearFounded: string;
    employeeCount: string;
    certifications?: string[];
    cleaningServices?: string[];
    floorcareServices?: string[];
    serviceAreas?: Array<{ city: string; state: string }>;
  };
  onSave: (data: any) => Promise<void>;
}

export function EditProfileSheet({ isOpen, onClose, companyData, onSave }: EditProfileSheetProps) {
  const [formData, setFormData] = useState(companyData);
  const [newCleaningService, setNewCleaningService] = useState("");
  const [newFloorcareService, setNewFloorcareService] = useState("");
  const [customCleaningService, setCustomCleaningService] = useState("");
  const [customFloorcareService, setCustomFloorcareService] = useState("");
  const [newServiceAreaCity, setNewServiceAreaCity] = useState("");
  const [newServiceAreaState, setNewServiceAreaState] = useState("");
  const [newCertification, setNewCertification] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get current user information for auto-population
  const { user } = useUser();
  const currentUser = useQuery(api.users.getCurrentUser, user?.id ? {} : "skip");

  // Mutation for logo upload
  const generateLogoUploadUrl = useMutation(api.organizations.generateLogoUploadUrl);

  // Update formData when companyData changes and auto-populate empty fields
  useEffect(() => {
    const updatedData = { ...companyData };

    // Auto-populate contactName if empty and user data is available
    if (!updatedData.contactName && currentUser?.name) {
      updatedData.contactName = currentUser.name;
    }

    setFormData(updatedData);
  }, [companyData, currentUser]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    const formattedPhone = formatPhoneNumber(value);
    setFormData((prev) => ({
      ...prev,
      [id]: formattedPhone,
    }));
  };

  const handleAddCertification = () => {
    if (newCertification.trim() && !formData.certifications?.includes(newCertification.trim())) {
      setFormData((prev) => ({
        ...prev,
        certifications: [...(prev.certifications || []), newCertification.trim()],
      }));
      setNewCertification("");
    }
  };

  const handleRemoveCertification = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      certifications: prev.certifications?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Error",
        description: "Please select an image file",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 5MB for storage)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "Image must be smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingLogo(true);

    try {
      // Step 1: Get upload URL from Convex storage
      const uploadUrl = await generateLogoUploadUrl();

      // Step 2: Upload file to Convex storage
      const uploadResponse = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      const { storageId } = await uploadResponse.json();

      // Step 3: Get the URL for the uploaded file to display it
      const logoUrl = await generateLogoUploadUrl({ storageId });

      // Update form data with the logo URL for display
      setFormData((prev) => ({
        ...prev,
        logo: logoUrl,
      }));

      toast({
        title: "Success!",
        description: "Logo uploaded successfully!",
      });
    } catch (error) {
      console.error("Logo upload error:", error);
      toast({
        title: "Error",
        description: "Failed to upload logo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSaveChanges = async () => {
    // Validate required fields (all except website and certifications)
    if (
      !formData.yearFounded ||
      !formData.address ||
      !formData.city ||
      !formData.state ||
      !formData.zip
    ) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    if (!formData.phone || !formData.email) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    if (!formData.employeeCount || !formData.description) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Error",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    // Validate year founded (should be a reasonable year)
    const currentYear = new Date().getFullYear();
    const yearFounded = parseInt(formData.yearFounded);
    if (isNaN(yearFounded) || yearFounded < 1800 || yearFounded > currentYear) {
      toast({
        title: "Error",
        description: "Please enter a valid year founded",
        variant: "destructive",
      });
      return;
    }

    // Validate employee count (should be a positive number)
    const employeeCount = parseInt(formData.employeeCount);
    if (isNaN(employeeCount) || employeeCount < 1) {
      toast({
        title: "Error",
        description: "Please enter a valid number of employees",
        variant: "destructive",
      });
      return;
    }

    // Validate services offered (at least one cleaning or floorcare service required)
    const hasCleaningServices = formData.cleaningServices && formData.cleaningServices.length > 0;
    const hasFloorcareServices =
      formData.floorcareServices && formData.floorcareServices.length > 0;
    if (!hasCleaningServices && !hasFloorcareServices) {
      toast({
        title: "Error",
        description: "Please add at least one cleaning or floorcare service",
        variant: "destructive",
      });
      return;
    }

    // Validate service areas (at least one required)
    if (!formData.serviceAreas || formData.serviceAreas.length === 0) {
      toast({
        title: "Error",
        description: "Please add at least one service area",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Exclude only name from save data (comes from Clerk)
      const { name, ...saveData } = formData;

      await onSave(saveData);

      toast({
        title: "Success!",
        description: "Company profile updated successfully!",
      });
      onClose();
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddCleaningService = () => {
    if (newCleaningService === "custom") {
      if (customCleaningService.trim() !== "") {
        setFormData((prev) => ({
          ...prev,
          cleaningServices: [...(prev.cleaningServices || []), customCleaningService],
        }));
        setCustomCleaningService("");
      }
    } else if (newCleaningService) {
      setFormData((prev) => ({
        ...prev,
        cleaningServices: [...(prev.cleaningServices || []), newCleaningService],
      }));
    }
    setNewCleaningService("");
  };

  const handleAddFloorcareService = () => {
    if (newFloorcareService === "custom") {
      if (customFloorcareService.trim() !== "") {
        setFormData((prev) => ({
          ...prev,
          floorcareServices: [...(prev.floorcareServices || []), customFloorcareService],
        }));
        setCustomFloorcareService("");
      }
    } else if (newFloorcareService) {
      setFormData((prev) => ({
        ...prev,
        floorcareServices: [...(prev.floorcareServices || []), newFloorcareService],
      }));
    }
    setNewFloorcareService("");
  };

  const handleRemoveCleaningService = (service: string) => {
    setFormData((prev) => ({
      ...prev,
      cleaningServices: (prev.cleaningServices || []).filter((s) => s !== service),
    }));
  };

  const handleRemoveFloorcareService = (service: string) => {
    setFormData((prev) => ({
      ...prev,
      floorcareServices: (prev.floorcareServices || []).filter((s) => s !== service),
    }));
  };

  const handleAddServiceArea = () => {
    if (newServiceAreaCity.trim() !== "" && newServiceAreaState !== "") {
      const newArea = {
        city: newServiceAreaCity.trim(),
        state: newServiceAreaState,
      };

      // Check if this area already exists
      const exists = (formData.serviceAreas || []).some(
        (area) => area.city === newArea.city && area.state === newArea.state
      );

      if (!exists) {
        setFormData((prev) => ({
          ...prev,
          serviceAreas: [...(prev.serviceAreas || []), newArea],
        }));
        setNewServiceAreaCity("");
        setNewServiceAreaState("");
      }
    }
  };

  const handleRemoveServiceArea = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      serviceAreas: (prev.serviceAreas || []).filter((_, i) => i !== index),
    }));
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Edit Company Profile</SheetTitle>
          <SheetDescription>Update your company information.</SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            <div className="mb-6 flex flex-col items-center text-center">
              <div className="relative mb-4">
                {formData.logo ? (
                  <img
                    src={formData.logo}
                    alt={formData.name}
                    className="max-h-24 max-w-24 rounded-lg border border-gray-200 object-contain"
                  />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-lg border border-gray-200 bg-gray-100">
                    <Building2 className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>

              <p className="mb-2 text-xs text-gray-500">
                Manage your logo in{" "}
                <a href="/settings?tab=branding" className="text-blue-600 hover:underline">
                  Branding Settings
                </a>
              </p>
            </div>

            {/* Company name, contact name, and job title are not editable here */}

            <div className="space-y-2">
              <Label htmlFor="yearFounded">
                Year Founded <span className="text-red-500">*</span>
              </Label>
              <Input
                id="yearFounded"
                value={formData.yearFounded}
                onChange={handleChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">
                Address <span className="text-red-500">*</span>
              </Label>
              <Input id="address" value={formData.address} onChange={handleChange} required />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">
                  City <span className="text-red-500">*</span>
                </Label>
                <Input id="city" value={formData.city} onChange={handleChange} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">
                  State <span className="text-red-500">*</span>
                </Label>
                <Select
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, state: value }))}
                  value={formData.state}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state.code} value={state.code}>
                        {state.name} - {state.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip">
                  ZIP Code <span className="text-red-500">*</span>
                </Label>
                <Input id="zip" value={formData.zip} onChange={handleChange} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">
                  Phone <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder="(555) 123-4567"
                  maxLength={14}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input id="website" value={formData.website} onChange={handleChange} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="employeeCount">
                Number of Employees <span className="text-red-500">*</span>
              </Label>
              <Input
                id="employeeCount"
                value={formData.employeeCount}
                onChange={handleChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">
                Company Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                required
              />
            </div>

            {/* Company Representative Section */}
            <div className="space-y-4">
              <div className="border-t pt-4">
                <h3 className="mb-4 text-lg font-semibold">Company Representative</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contactName">Contact Name</Label>
                    <Input
                      id="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      placeholder="Primary contact person"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="jobTitle">Job Title</Label>
                    <Input
                      id="jobTitle"
                      value={formData.jobTitle}
                      onChange={handleChange}
                      placeholder="e.g., Owner, Manager"
                    />
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contactEmail">Contact Email</Label>
                    <Input
                      id="contactEmail"
                      type="email"
                      value={formData.contactEmail}
                      onChange={handleChange}
                      placeholder="contact@company.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contactPhone">Contact Phone</Label>
                    <Input
                      id="contactPhone"
                      value={formData.contactPhone}
                      onChange={handlePhoneChange}
                      placeholder="(555) 123-4567"
                      maxLength={14}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Certifications</Label>
              <div className="mb-2 flex flex-wrap gap-2">
                {(formData.certifications || []).map((certification, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="flex items-center gap-1 px-3 py-1 text-sm"
                  >
                    {certification}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-1 h-4 w-4 p-0"
                      onClick={() => handleRemoveCertification(index)}
                    >
                      <X className="h-3 w-3 text-gray-500" />
                    </Button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  className="flex-1"
                  placeholder="Add a certification"
                  value={newCertification}
                  onChange={(e) => setNewCertification(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCertification();
                    }
                  }}
                />
                <Button
                  onClick={handleAddCertification}
                  disabled={!newCertification.trim()}
                  className="flex-shrink-0"
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add
                </Button>
              </div>
            </div>

            <Separator className="my-4" />

            <div className="space-y-6">
              <h2 className="text-lg font-semibold">
                Services Offered <span className="text-red-500">*</span>
              </h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-md mb-2 flex items-center gap-2 font-medium">
                    <Sparkles className="h-4 w-4 text-blue-500" /> Cleaning Services
                  </h3>

                  <div className="mb-3 flex flex-wrap gap-2">
                    {(formData.cleaningServices || []).map((service, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="flex items-center gap-1 px-3 py-1 text-sm"
                      >
                        {service}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="ml-1 h-4 w-4 p-0"
                          onClick={() => handleRemoveCleaningService(service)}
                        >
                          <Trash2 className="h-3 w-3 text-gray-500" />
                        </Button>
                      </Badge>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Select value={newCleaningService} onValueChange={setNewCleaningService}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a cleaning service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="custom">Add Custom Service</SelectItem>
                          <SelectItem value="General Cleaning">General Cleaning</SelectItem>
                          <SelectItem value="Post Construction Cleaning">
                            Post Construction Cleaning
                          </SelectItem>
                          <SelectItem value="Disinfectant Cleaning">
                            Disinfectant Cleaning
                          </SelectItem>
                          <SelectItem value="Restroom Cleaning">Restroom Cleaning</SelectItem>
                          <SelectItem value="Exterior Cleaning">Exterior Cleaning</SelectItem>
                          <SelectItem value="Pressure Washing">Pressure Washing</SelectItem>
                          <SelectItem value="Tile & Grout Cleaning">
                            Tile & Grout Cleaning
                          </SelectItem>
                          <SelectItem value="Window & Glass Cleaning">
                            Window & Glass Cleaning
                          </SelectItem>
                          <SelectItem value="Trash Chute & Dumpster Area Cleaning">
                            Trash Chute & Dumpster Area Cleaning
                          </SelectItem>
                          <SelectItem value="Commercial Kitchen Cleaning">
                            Commercial Kitchen Cleaning
                          </SelectItem>
                          <SelectItem value="Graffiti Removal">Graffiti Removal</SelectItem>
                          <SelectItem value="Odor Removal">Odor Removal</SelectItem>
                          <SelectItem value="Upholstery & Fabric Cleaning">
                            Upholstery & Fabric Cleaning
                          </SelectItem>
                          <SelectItem value="Warehouse & Storage Area Cleaning">
                            Warehouse & Storage Area Cleaning
                          </SelectItem>
                          <SelectItem value="High-Touch Surface Sanitization">
                            High-Touch Surface Sanitization
                          </SelectItem>
                          <SelectItem value="Equipment & Machinery Cleaning">
                            Equipment & Machinery Cleaning
                          </SelectItem>
                          <SelectItem value="Specialty Cleaning">Specialty Cleaning</SelectItem>
                          <SelectItem value="Move-In/Move-Out Cleaning">
                            Move-In/Move-Out Cleaning
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={handleAddCleaningService}
                      disabled={!newCleaningService}
                      size="default"
                      className="px-3"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {newCleaningService === "custom" && (
                    <div className="mt-2">
                      <Input
                        placeholder="Enter custom cleaning service"
                        value={customCleaningService}
                        onChange={(e) => setCustomCleaningService(e.target.value)}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-md mb-2 flex items-center gap-2 font-medium">
                    <Layers className="h-4 w-4 text-blue-500" /> Floorcare Services
                  </h3>

                  <div className="mb-3 flex flex-wrap gap-2">
                    {(formData.floorcareServices || []).map((service, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="flex items-center gap-1 px-3 py-1 text-sm"
                      >
                        {service}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="ml-1 h-4 w-4 p-0"
                          onClick={() => handleRemoveFloorcareService(service)}
                        >
                          <Trash2 className="h-3 w-3 text-gray-500" />
                        </Button>
                      </Badge>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Select value={newFloorcareService} onValueChange={setNewFloorcareService}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a floorcare service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="custom">Add Custom Service</SelectItem>
                          <SelectItem value="Carpet Cleaning">Carpet Cleaning</SelectItem>
                          <SelectItem value="Carpet Extraction">Carpet Extraction</SelectItem>
                          <SelectItem value="Floor Stripping and Waxing">
                            Floor Stripping and Waxing
                          </SelectItem>
                          <SelectItem value="Floor Top Scrubbing and Waxing">
                            Floor Top Scrubbing and Waxing
                          </SelectItem>
                          <SelectItem value="Floor Stripping">Floor Stripping</SelectItem>
                          <SelectItem value="Floor Waxing">Floor Waxing</SelectItem>
                          <SelectItem value="Floor Buffing">Floor Buffing</SelectItem>
                          <SelectItem value="Tile & Grout Cleaning">
                            Tile & Grout Cleaning
                          </SelectItem>
                          <SelectItem value="Concrete Polishing">Concrete Polishing</SelectItem>
                          <SelectItem value="Hardwood Floor Refinishing">
                            Hardwood Floor Refinishing
                          </SelectItem>
                          <SelectItem value="Stone Floor Restoration">
                            Stone Floor Restoration
                          </SelectItem>
                          <SelectItem value="Vinyl Floor Maintenance">
                            Vinyl Floor Maintenance
                          </SelectItem>
                          <SelectItem value="Marble Floor Polishing">
                            Marble Floor Polishing
                          </SelectItem>
                          <SelectItem value="Terrazzo Floor Restoration">
                            Terrazzo Floor Restoration
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={handleAddFloorcareService}
                      disabled={!newFloorcareService}
                      size="default"
                      className="px-3"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {newFloorcareService === "custom" && (
                    <div className="mt-2">
                      <Input
                        placeholder="Enter custom floorcare service"
                        value={customFloorcareService}
                        onChange={(e) => setCustomFloorcareService(e.target.value)}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Separator className="my-6" />

            <div className="space-y-4">
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <MapPin className="h-4 w-4 text-blue-500" /> Service Areas{" "}
                <span className="text-red-500">*</span>
              </h3>

              <div className="mb-3 flex flex-wrap gap-2">
                {(formData.serviceAreas || []).map((area, index) => (
                  <Badge
                    key={index}
                    variant="outline"
                    className="flex items-center gap-1 px-3 py-1 text-sm"
                  >
                    {area.city}, {area.state}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-1 h-4 w-4 p-0"
                      onClick={() => handleRemoveServiceArea(index)}
                    >
                      <Trash2 className="h-3 w-3 text-gray-500" />
                    </Button>
                  </Badge>
                ))}
              </div>

              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    placeholder="City"
                    value={newServiceAreaCity}
                    onChange={(e) => setNewServiceAreaCity(e.target.value)}
                  />
                </div>
                <div className="flex-1">
                  <Select
                    onValueChange={(value) => setNewServiceAreaState(value)}
                    value={newServiceAreaState}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name} - {state.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleAddServiceArea}
                  disabled={!newServiceAreaCity.trim() || !newServiceAreaState}
                  size="default"
                  className="px-3"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Button className="mt-6 w-full" onClick={handleSaveChanges} disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
